import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { FaTimes, FaCheck } from "react-icons/fa";
import { editUserGroup, getAllGroup, getAllUserByGroup, getAllUserByRole, getGroup, saveUser, saveUserTec } from "../api/appliApi";
import { format } from "date-fns";
import fr from "date-fns/locale/fr";
import FilterComponent from "../components/FilterComponent";
import CustomLoader from "../components/LoaderComponent";
import { customHeader, paginationOptions } from "../utils/StyledDataTable";
import { Form } from "react-bootstrap";
import { FormModal } from "../components/FormModal";
import { MdEdit } from "react-icons/md";
import { ToastContainer, toast } from "react-toastify";
import { AuthContext } from "../context/AuthContext";
import { DisplayRoleProps, FormElement, UserAdminDto, UserAdminInterface, UserUpdateDto } from "../utils/Type";
import MultiSelectDropdown from "../components/MultiSelectDropdown";
import { TableContainer } from "../utils/StyledComponents";
import { ConditionalStyles } from "react-data-table-component";
import plusCircle from "../asset/img/icon/plus-circle.svg";

const clearFormData = () => ({
    id: 0,
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    roleName: "",
    locale: "",
    groups: [] as string[],
    active: false
});

const UserAdmin = () => {
    const [filterText, setFilterText] = useState('');
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [pending, setPending] = useState(true);
    const [isEdit, setIsEdit] = useState(false);
    const [datas, setDatas] = useState<UserAdminInterface[]>([]);
    const [modalShow, setModalShow] = useState(false);
    const [selectedName, setSelectedName] = useState('');
    const auth = useContext(AuthContext);
    const [groups, setGroups] = useState<{ id: string, name: string }[]>([]);
    const [roleFiltre, setRoleFiltre] = useState<"superadmin" | "technician">("superadmin");
    const [formData, setFormData] = useState(clearFormData());
    const submitLockRef = useRef(false);
    const [isSaving, setIsSaving] = useState(false);

    const roleLabels: { [key: string]: string } = {
        superadmin: "Super administrateur",
        admin: "Administrateur",
        technician: "Technicien"
    };

    const DisplayRole: React.FC<DisplayRoleProps> = ({ roleKey }) => {
        return <>{roleLabels[roleKey]}</>;
    };

    const fetchData = useCallback(async () => {
        setPending(true);

        try {
            let data;

            if (auth?.role === "superadmin") {
                data = await getAllUserByRole(roleFiltre);
            } else if (auth?.role === "admin") {
                data = await getAllUserByGroup(auth?.userId);
            }

            if (Array.isArray(data.data)) {
                setDatas(data.data);
            } else {
                setDatas([]);
            }
        } catch (error) {
            setDatas([]);
        } finally {
            setPending(false);
        }
    }, [auth?.role, auth?.userId, roleFiltre]);

    const getGroupeUser = useCallback(async () => {
        try {
            if (auth?.role === "superadmin") {
                const data = await getAllGroup();
                setGroups(data.data.map((group: { id: string, name: string }) => ({ id: group.id, name: group.name })));
            } else {
                const storedGroupes = localStorage.getItem("groupes");
                const groupesIds: number[] = storedGroupes ? JSON.parse(storedGroupes) : [];
                if (groupesIds.length === 0) {
                    setGroups([]);
                    return;
                }
                const data = await getGroup(groupesIds.map(id => id.toString()));
                setGroups(data.data.map((group: { id: string, name: string }) => ({ id: group.id, name: group.name })));
            }

        } catch (error) {
            setGroups([]);
        }
    }, [auth?.role]);

    useEffect(() => {
        fetchData();
        getGroupeUser();
    }, [fetchData, getGroupeUser]);

    const filteredData = useMemo(() => {
        const txt = filterText.toLowerCase();
        return datas.filter(d =>
            d.id.toString().includes(txt) ||
            d.email.toLowerCase().includes(txt) ||
            d.firstName.toLowerCase().includes(txt) ||
            d.lastName.toLowerCase().includes(txt) ||
            d.locale.toLowerCase().includes(txt) ||
            (d.groups ?? []).some(group => group.name.toLowerCase().includes(txt))
        );
    }, [filterText, datas]);

    const handleFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilterText(event.target.value);
    };

    const columns = [
        {
            name: 'Id',
            selector: (row: { id: number }) => row.id,
            sortable: true,
            width: '7%'
        },
        {
            name: 'Nom',
            selector: (row: { lastName: string }) => row.lastName,
            sortable: true,
        },
        {
            name: 'Prénom',
            selector: (row: { firstName: string }) => row.firstName,
            sortable: true,
        },
        {
            name: 'Adresse email',
            selector: (row: { email: string }) => row.email,
            sortable: true,
        },
        {
            name: 'Rôle',
            cell: (row: any) => (
                <DisplayRole roleKey={row.roleName} />
            ),
            sortable: true,
        },
        {
            name: 'Groupe',
            selector: (row: UserAdminInterface) => (row.groups ?? []).map(g => g.name).join(', '),
            sortable: true,
        },
        {
            name: 'Active',
            selector: (row: { active: boolean }) => row.active,
            sortable: true,
            width: '7%',
            cell: (row: { active: boolean }) => row.active ? <FaCheck /> : <FaTimes />
        },
        {
            name: 'Date de création',
            selector: (row: UserAdminInterface) => format(new Date(row.createdAt), "dd/MM/yyyy HH:mm", { locale: fr }),
            sortable: true,
            width: '11%'
        },
        {
            name: 'Date de modification',
            selector: (row: UserAdminInterface) => format(new Date(row.updatedAt), "dd/MM/yyyy HH:mm", { locale: fr }),
            sortable: true,
            width: '11%'
        },
        {
            name: 'Action',
            cell: (row: any) => (
                <>
                    <MdEdit size={24} onClick={() => handleEdit(row)} style={{ cursor: "pointer" }} />
                </>
            ),
            ignoreRowClick: true
        }
    ];

    const conditionalRowStyles: ConditionalStyles<UserAdminInterface>[] = [
        {
            when: row => filteredData.indexOf(row) % 2 !== 0,
            style: { backgroundColor: "#F6F8FD" },
        },
    ];

    const handleClear = () => {
        if (filterText) {
            setResetPaginationToggle(!resetPaginationToggle);
            setFilterText('');
        }
    };

    const handleChange = (e: React.ChangeEvent<FormElement>) => {
        const { name, value } = e.target;

        if (e.target instanceof HTMLInputElement && e.target.type === "checkbox") {
            setFormData({
                ...formData,
                [name]: e.target.checked,
            });
        } else {
            setFormData({
                ...formData,
                [name]: name === "email" ? value.toLowerCase() : value,
            });
        }
    };

    const notify = (message: string) => toast(message);
    const notifyError = (message: string) => toast.error(message);

    const handleClearForm = () => { setFormData(clearFormData()); setSelectedName(''); };

    const handleValidate = async () => {
        if (submitLockRef.current || isSaving) return;

        submitLockRef.current = true;
        setIsSaving(true);

        try {
            if (!formData.lastName.trim()) {
                notifyError("Le nom est requis.");
                return;
            }

            if (!formData.id && auth?.role === "superadmin" && !formData.roleName.trim()) {
                notifyError("Le rôle est requis.");
                return;
            }

            if (formData.id) {
                if ((auth?.role === "admin") || (auth?.role === "superadmin" && formData.roleName === "technician")) {
                    const selectedGroups = formData.groups
                        .map(id => groups.find(g => g.id.toString() === id))
                        .filter((g): g is { id: string; name: string } => g !== undefined);

                    const targetDto: UserAdminDto = {
                        id: formData.id,
                        email: formData.email,
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        roleName: formData.roleName,
                        locale: formData.locale,
                        active: formData.active,
                        groups: selectedGroups.map(g => ({ id: g.id, name: g.name })),
                    };

                    const actingDto: UserAdminDto = {
                        id: auth!.user.data.id,
                        email: auth!.user.data.email,
                        firstName: auth!.user.data.firstName,
                        lastName: auth!.user.data.lastName,
                        roleName: auth!.user.data.roleName,
                        locale: auth!.user.data.locale,
                        active: true,
                    };

                    const payload: UserUpdateDto = { targetDto, actingDto };
                    await editUserGroup.put(payload);
                } else {
                    const selectedGroups = formData.groups
                        .map(id => groups.find(g => g.id === id))
                        .filter((g): g is { id: string; name: string } => g !== undefined);

                    const targetDto: UserAdminDto = {
                        id: formData.id,
                        email: formData.email,
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        roleName: formData.roleName,
                        locale: formData.locale,
                        active: formData.active,
                        groups: selectedGroups.map(g => ({ id: g.id, name: g.name })),
                    };

                    const actingDto: UserAdminDto = {
                        id: auth!.user.data.id,
                        email: auth!.user.data.email,
                        firstName: auth!.user.data.firstName,
                        lastName: auth!.user.data.lastName,
                        roleName: auth!.user.data.roleName,
                        locale: auth!.user.data.locale,
                        active: true,
                    };

                    const payload: UserUpdateDto = { targetDto, actingDto };
                    await editUserGroup.put(payload);
                }
            } else {
                if (auth?.role === "admin") {
                    await saveUserTec.post(formData);
                } else {
                    await saveUser.post(formData);
                }
            }

            await fetchData();
            handleClearForm();
            notify("Enregistrer avec succès !");
            setModalShow(false);
            setIsEdit(false);
        } catch (error: any) {
            notifyError("Erreur lors de l'enregistrement.\n" + (error?.message ?? error));
        } finally {
            setIsSaving(false);
            submitLockRef.current = false;
        }
    };

    const handleEdit = (item: UserAdminInterface) => {
        setIsEdit(true);
        if ((auth?.role === "admin") || (auth?.role === "superadmin" && item.roleName === "technician")) {
            const userGroupIdsAsString = item.groups?.map(g => g.id.toString()) ?? [];

            setFormData({
                id: item.id,
                email: item.email,
                password: "",
                firstName: item.firstName,
                lastName: item.lastName,
                roleName: item.roleName,
                locale: item.locale,
                groups: userGroupIdsAsString,
                active: item.active
            });
        } else {
            const userGroupIdsAsString = item.groups?.map(g => g.id) ?? [];

            setFormData({
                id: item.id,
                email: item.email,
                password: "",
                firstName: item.firstName,
                lastName: item.lastName,
                roleName: item.roleName,
                locale: item.locale,
                groups: userGroupIdsAsString,
                active: item.active
            });
        }
        setSelectedName(item.lastName);
        setModalShow(true);
    };

    return (
        <div className="custom-content">
            <div className="custom-header">
                <span className="custom-title">{auth?.role === "superadmin" ? "Liste des utilisateurs" : "Liste des techniciens"}</span>
                <button className="btn-add-circle" onClick={() => setModalShow(true)}>
                    <span className="btn-text">{auth?.role === "superadmin" ? "Ajouter un utilisateur" : "Ajouter un technicien"}</span>
                    <span className="plus-icon-circle"><img src={plusCircle} alt="icone +" /></span>
                </button>
            </div>

            <ToastContainer />
            <div style={{ display: 'flex' }}>
                <div style={{ flex: 1 }}>
                    <FilterComponent
                        filterText={filterText}
                        onFilter={handleFilter}
                        onClear={handleClear}
                    />
                </div>

                {auth?.role === "superadmin" && (
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <label className="form-label mb-0">Rôle :</label>
                        <button
                            type="button"
                            className={`btn ${roleFiltre === "technician" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => setRoleFiltre("technician")}
                        >
                            Tout
                        </button>
                        <button
                            type="button"
                            className={`btn ${roleFiltre === "superadmin" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => setRoleFiltre("superadmin")}
                        >
                            Admin & Super admin
                        </button>
                    </div>
                )}
            </div>

            <TableContainer>
                <DataTable
                    columns={columns}
                    data={filteredData}
                    pagination
                    paginationResetDefaultPage={resetPaginationToggle}
                    conditionalRowStyles={conditionalRowStyles}
                    persistTableHead
                    fixedHeader
                    fixedHeaderScrollHeight="calc(100vh - 150px)"
                    progressPending={pending}
                    progressComponent={<CustomLoader />}
                    customStyles={customHeader}
                    paginationComponentOptions={paginationOptions}
                    noDataComponent={<p style={{ marginTop: "20px" }}>Aucun enregistrement trouvé.</p>}
                />
            </TableContainer>

            <FormModal
                show={modalShow}
                onValidate={handleValidate}
                onAbort={() => {
                    if (isSaving) return;
                    setModalShow(false);
                    handleClearForm();
                    setIsEdit(false);
                }}
                title={`Détails de l'utilisateur${selectedName ? ` "${selectedName}"` : ''}`}
                body={
                    <>
                        <Form>
                            <Form.Group className="mb-3" controlId="user.ControlInput">
                                <Form.Label>Nom</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Nom"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    autoFocus
                                    className="uppercase-input"
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Prénom</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Prénom"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Adresse mail</Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder="Adresse email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </Form.Group>

                            {auth?.role === "superadmin" && !isEdit && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Rôle</Form.Label>
                                    <Form.Select
                                        name="roleName"
                                        value={formData.roleName}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">-- Sélectionner un rôle --</option>
                                        <option value="admin">Administrateur</option>
                                        <option value="superadmin">Super Administrateur</option>
                                    </Form.Select>
                                </Form.Group>
                            )}

                            {(auth?.role === "admin" || auth?.role === "superadmin") && (
                                <Form.Group className="mb-3">
                                    {(auth?.role === "admin" || (auth?.role === "superadmin" && isEdit && formData.roleName !== "superadmin")) && (
                                        <Form.Label>Groupes</Form.Label>
                                    )}
                                    {(auth?.role === "admin" || (auth?.role === "superadmin" && isEdit && formData.roleName === "technician")) && (
                                        <div className="p-2 rounded border d-flex flex-wrap gap-3">
                                            {(auth?.role === "admin" || (auth?.role === "superadmin" && isEdit && formData.roleName === "technician")) && groups.map(g => (
                                                <Form.Check
                                                    key={g.id}
                                                    type="radio"
                                                    id={`group-${g.id}`}
                                                    name="groups"
                                                    value={g.id.toString()}
                                                    label={g.name}
                                                    checked={formData.groups[0] === g.id.toString()}
                                                    onChange={e => {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            groups: [e.target.value]
                                                        }));
                                                    }}
                                                    required
                                                />
                                            ))}
                                        </div>
                                    )}

                                    {auth?.role === "superadmin" && isEdit && formData.roleName === "admin" && (<>
                                        <MultiSelectDropdown
                                            options={groups.map((g) => ({ value: g.id, label: g.name }))}
                                            value={formData.groups}
                                            onChange={(selected: string[]) =>
                                                setFormData((prev) => ({ ...prev, groups: selected }))
                                            }
                                        />
                                    </>
                                    )}

                                </Form.Group>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>Localisation</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Localisation"
                                    name="locale"
                                    value={formData.locale}
                                    onChange={handleChange}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3 d-flex align-items-center">
                                <Form.Check
                                    type="switch"
                                    id="switch-active"
                                    label="Actif"
                                    name="active"
                                    checked={formData.active}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Form>

                    </>
                }
                isBusy={isSaving}
                btnValidate={isSaving ? "Enregistrement..." : "Enregistrer"}
                abortDisabled={isSaving}
            />

        </div>

    );
};

export default UserAdmin;
